import { useCallback } from 'react'

import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import BaseProduct from '../domains/Product/BaseProduct'
import Product from '../domains/Product/Product'
import ProductTag from '../domains/ProductTag'
import { ProductVariantOption } from '../domains/ProductVariant'
import productHelper from '../helpers/productHelper'
import wishlistStateManager from '../redux/globalStateManager/WishlistStateManager'
import wishlistService from '../services/wishlistService'
import { getAddToWishlistExtraTrackingData } from '../utils/wishlist.utils'

export const useWishList = () => {
  const isItemInWishList = useCallback(
    (product: Product | { spid: number }, selectedOptions: ProductVariantOption[]) => {
      return wishlistService.isItemInWishlist(product.spid, getSelectedColorFromSelectedOptions(selectedOptions) ?? '')
    },
    [],
  )

  const removeItemFromWishList = useCallback(
    async (
      product: BaseProduct,
      selectedOptions: ProductVariantOption[],
      trackerSection?: string | undefined,
      wishlistTrackConfig?: Record<string, any>,
      applicableTags?: ProductTag[] | undefined,
    ) => {
      const selectedColor = getSelectedColorFromSelectedOptions(selectedOptions) ?? ''
      const price = productHelper.getQuincePriceForOptions(product as Product, selectedOptions)
      let trackingContext = undefined
      if (price) {
        trackingContext = {
          product_price: price,
          clicked_prodid: `${product.id}_${selectedColor}`,
        }
      }
      const variantsForSelectedOptions = productHelper.getVariantsForOptions(product, selectedOptions)
      await wishlistService.removeFromWishlist(product.spid, selectedColor)
      trackEvent(TELEMETRY_EVENT_NAMES.REMOVE_FROM_WISHLIST, {
        productId: product.spid,
        color: selectedColor,
        trackerSection: trackerSection,
        tags: applicableTags,
        context: {
          ...(wishlistTrackConfig ?? trackingContext ?? {}),
          productProperties: getProductPropertiesForTracking(
            product as Product,
            variantsForSelectedOptions[0]?.svid?.toString(),
          ),
        },
      })
      wishlistStateManager.setWishlistTotalItemsCount(wishlistService.getWishlistTotalItemsCount())
    },
    [],
  )

  const addItemToWishList = useCallback(
    async (
      product: Product | { spid: number; internalProductId_color?: string; price?: number },
      selectedOptions: ProductVariantOption[],
      trackerSection?: string | undefined,
      productPosition?: number,
      productSearchQuery?: string,
      wishlistTrackConfig?: Record<string, any>,
      applicableTags?: ProductTag[] | undefined,
    ) => {
      const selectedColor = getSelectedColorFromSelectedOptions(selectedOptions)
      await wishlistService.addItemToWishlist(product.spid, selectedColor ?? '')
      const price = productHelper.getQuincePriceForOptions(product as Product, selectedOptions)
      let trackingContext = undefined

      if ('id' in product) {
        trackingContext = {
          product_price: price,
          clicked_prodid: `${product.id}_${selectedColor}`,
        }
      }

      if ('internalProductId_color' in product) {
        trackingContext = {
          product_price: product.price,
          clicked_prodid: product.internalProductId_color,
        }
      }

      trackEvent(TELEMETRY_EVENT_NAMES.ADD_TO_WISHLIST, {
        productId: product.spid,
        color: selectedColor,
        trackerSection: trackerSection,
        product_search_query: productSearchQuery,
        product_position: productPosition,
        tags: applicableTags,
        count_color_variant: selectedColor?.length,
        ...getAddToWishlistExtraTrackingData(product),
        context: {
          ...(wishlistTrackConfig ?? trackingContext ?? {}),
        },
      })
      wishlistStateManager.setWishlistTotalItemsCount(wishlistService.getWishlistTotalItemsCount())
    },
    [],
  )
  return { isItemInWishList, addItemToWishList, removeItemFromWishList }
}
